/**
 * Time-series charts: sparkline, trend, diverging bars, stacked bars.
 *
 * All four share the category-index model — the x axis is a list of labelled
 * slots, not a continuous time scale. Every series in this console is daily and
 * gap-free, so a band scale is both simpler and more honest than a time scale
 * that would silently interpolate across missing days.
 */

import { Fragment, useMemo } from 'react'
import {
  AxisBottom,
  AxisLeft,
  ChartShell,
  Crosshair,
  seriesColor,
  toneColor,
  useCategoryHover,
  useChartTooltip,
  type Plot,
} from './chart-kit'
import {
  areaPath,
  bandScale,
  barPath,
  linearScale,
  linePath,
  movingAverage,
  niceDomain,
  niceTicks,
} from './scale'

/* ------------------------------------------------------------ sparkline -- */

export interface SparklineProps {
  values: number[]
  width?: number
  height?: number
  /** CSS colour. Defaults to tone of the last value vs the first. */
  color?: string
  /** Fill under the line. */
  area?: boolean
  /** Draw a dot on the final point — anchors "where it ended". */
  marker?: boolean
  label?: string
}

/** A trend glyph, not a chart: no axes, no interaction, never a lone number's replacement. */
export function Sparkline({
  values,
  width = 72,
  height = 24,
  color,
  area = true,
  marker = true,
  label,
}: SparklineProps) {
  const clean = values.filter((v) => Number.isFinite(v))
  if (clean.length < 2) return <span style={{ display: 'inline-block', width, height }} />

  const [min, max] = niceDomain(clean, { includeZero: false, padding: 0.12 })
  const x = bandScale(clean.length, [1, width - 1], 0)
  const y = linearScale([min, max], [height - 2, 2])
  const points = clean.map((v, i) => [x.center(i), y(v)] as [number, number])
  const first = clean[0] ?? 0
  const last = clean[clean.length - 1] ?? 0
  const stroke = color ?? toneColor(last - first)

  return (
    <svg
      width={width}
      height={height}
      role="img"
      aria-label={label ?? `Trend over the last ${clean.length} points`}
      style={{ display: 'block', overflow: 'visible' }}
    >
      {area ? (
        <path d={areaPath(points, height, true)} fill={stroke} fillOpacity={0.14} stroke="none" />
      ) : null}
      <path d={linePath(points, true)} fill="none" stroke={stroke} strokeWidth={1.6} strokeLinecap="round" />
      {marker ? (
        <circle cx={points[points.length - 1]![0]} cy={points[points.length - 1]![1]} r={2.1} fill={stroke} />
      ) : null}
    </svg>
  )
}

/* ---------------------------------------------------------------- trend -- */

export interface TrendSeries {
  id: string
  label: string
  values: (number | null)[]
  color?: string
  /** `bar` renders behind lines, so a volume series never hides a P&L curve. */
  kind?: 'line' | 'area' | 'bar'
  /** Plot against a second axis with its own domain. */
  axis?: 'left' | 'right'
  dashed?: boolean
}

export interface TrendChartProps {
  categories: string[]
  series: TrendSeries[]
  height?: number
  /** Formats the left axis and tooltip values. */
  format: (value: number) => string
  formatRight?: (value: number) => string
  /** Overlay a centred moving average of the first series. */
  trendWindow?: number
  label: string
  emptyMessage?: string
  maxLabels?: number
  /** Force the value axis to include zero. */
  includeZero?: boolean
}

export function TrendChart({
  categories,
  series,
  height = 220,
  format,
  formatRight,
  trendWindow,
  label,
  emptyMessage,
  maxLabels = 7,
  includeZero = true,
}: TrendChartProps) {
  const tooltip = useChartTooltip()
  const hasData = categories.length > 0 && series.some((s) => s.values.some((v) => v !== null))

  const leftValues = series.filter((s) => s.axis !== 'right').flatMap((s) => s.values.filter((v): v is number => v !== null))
  const rightValues = series.filter((s) => s.axis === 'right').flatMap((s) => s.values.filter((v): v is number => v !== null))

  const margin = rightValues.length ? { right: 52 } : undefined

  return (
    <ChartShell
      height={height}
      label={label}
      isEmpty={!hasData}
      empty={emptyMessage}
      margin={margin}
    >
      {(plot) => (
        <TrendBody
          plot={plot}
          categories={categories}
          series={series}
          leftValues={leftValues}
          rightValues={rightValues}
          format={format}
          formatRight={formatRight ?? format}
          trendWindow={trendWindow}
          maxLabels={maxLabels}
          includeZero={includeZero}
          tooltip={tooltip}
        />
      )}
    </ChartShell>
  )
}

function TrendBody({
  plot,
  categories,
  series,
  leftValues,
  rightValues,
  format,
  formatRight,
  trendWindow,
  maxLabels,
  includeZero,
  tooltip,
}: {
  plot: Plot
  categories: string[]
  series: TrendSeries[]
  leftValues: number[]
  rightValues: number[]
  format: (v: number) => string
  formatRight: (v: number) => string
  trendWindow: number | undefined
  maxLabels: number
  includeZero: boolean
  tooltip: ReturnType<typeof useChartTooltip>
}) {
  const { margin, inner } = plot
  const x = bandScale(categories.length, [0, inner.width], 0.25)

  const leftDomain = niceDomain(leftValues, { includeZero })
  const yLeft = linearScale(leftDomain, [inner.height, 0])
  const leftTicks = niceTicks(leftDomain[0], leftDomain[1], 4)

  const rightDomain = niceDomain(rightValues, { includeZero })
  const yRight = linearScale(rightDomain, [inner.height, 0])

  const { hovered, overlay } = useCategoryHover(plot, categories.length, x.indexAt)

  const trend = useMemo(() => {
    if (!trendWindow) return null
    const base = series[0]
    if (!base) return null
    return movingAverage(base.values.map((v) => v ?? 0), trendWindow)
  }, [series, trendWindow])

  // Push the hovered slice into the shared tooltip.
  const onMove = (event: React.PointerEvent<SVGGElement>) => {
    if (hovered === null) return
    tooltip.show({
      x: event.clientX,
      y: event.clientY,
      title: categories[hovered] ?? '',
      rows: series.map((s, i) => ({
        label: s.label,
        color: s.color ?? seriesColor(i),
        value:
          s.values[hovered] === null || s.values[hovered] === undefined
            ? '—'
            : (s.axis === 'right' ? formatRight : format)(s.values[hovered]!),
      })),
    })
  }

  return (
    <g onPointerMove={onMove} onPointerLeave={tooltip.hide}>
      <AxisLeft plot={plot} ticks={leftTicks} scale={yLeft} format={format} />
      <AxisBottom
        plot={plot}
        count={categories.length}
        position={x.center}
        format={(i) => categories[i] ?? ''}
        maxLabels={maxLabels}
      />
      {rightValues.length ? (
        <g>
          {niceTicks(rightDomain[0], rightDomain[1], 4).map((t) => (
            <text
              key={t}
              x={margin.left + inner.width + 8}
              y={margin.top + yRight(t)}
              dominantBaseline="middle"
              fill="var(--w7-axis)"
              fontSize={10}
              style={{ fontVariantNumeric: 'tabular-nums' }}
            >
              {formatRight(t)}
            </text>
          ))}
        </g>
      ) : null}

      <g transform={`translate(${margin.left} ${margin.top})`}>
        {/* Bars render first so lines always sit on top. */}
        {series
          .filter((s) => s.kind === 'bar')
          .map((s, i) => {
            const scale = s.axis === 'right' ? yRight : yLeft
            const zero = scale(0)
            return (
              <g key={s.id}>
                {s.values.map((v, j) => {
                  if (v === null) return null
                  const top = Math.min(zero, scale(v))
                  const h = Math.abs(scale(v) - zero)
                  return (
                    <path
                      key={j}
                      d={barPath(x(j), top, x.bandwidth, h, [2, 2, 0, 0])}
                      fill={s.color ?? seriesColor(i)}
                      fillOpacity={hovered === null || hovered === j ? 0.5 : 0.24}
                    />
                  )
                })}
              </g>
            )
          })}

        {series
          .filter((s) => s.kind !== 'bar')
          .map((s, i) => {
            const scale = s.axis === 'right' ? yRight : yLeft
            const points = s.values
              .map((v, j) => (v === null ? null : ([x.center(j), scale(v)] as [number, number])))
              .filter((p): p is [number, number] => p !== null)
            const color = s.color ?? seriesColor(i)
            return (
              <Fragment key={s.id}>
                {s.kind === 'area' ? (
                  <path d={areaPath(points, scale(leftDomain[0]), true)} fill={color} fillOpacity={0.12} />
                ) : null}
                <path
                  d={linePath(points, true)}
                  fill="none"
                  stroke={color}
                  strokeWidth={1.9}
                  strokeDasharray={s.dashed ? '4 4' : undefined}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </Fragment>
            )
          })}

        {trend ? (
          <path
            d={linePath(
              trend
                .map((v, j) => (v === null ? null : ([x.center(j), yLeft(v)] as [number, number])))
                .filter((p): p is [number, number] => p !== null),
              true,
            )}
            fill="none"
            stroke="var(--w7-text-3)"
            strokeWidth={1.4}
            strokeDasharray="5 4"
          />
        ) : null}

        {hovered !== null
          ? series
              .filter((s) => s.kind !== 'bar')
              .map((s, i) => {
                const v = s.values[hovered]
                if (v === null || v === undefined) return null
                const scale = s.axis === 'right' ? yRight : yLeft
                return (
                  <circle
                    key={s.id}
                    cx={x.center(hovered)}
                    cy={scale(v)}
                    r={3.4}
                    fill="var(--w7-bg-page)"
                    stroke={s.color ?? seriesColor(i)}
                    strokeWidth={2}
                  />
                )
              })
          : null}
      </g>

      {hovered !== null ? <Crosshair plot={plot} x={x.center(hovered)} /> : null}
      {overlay}
    </g>
  )
}

/* ------------------------------------------------------- diverging bars -- */

export interface DivergingBarsProps {
  categories: string[]
  values: number[]
  height?: number
  format: (value: number) => string
  label: string
  emptyMessage?: string
  maxLabels?: number
  /** Optional cumulative line drawn on the same axis. */
  cumulativeValues?: number[]
  cumulativeLabel?: string
  valueLabel?: string
}

/**
 * Daily P&L. Bars above and below a zero rule, coloured by sign.
 *
 * Colour and position both encode the sign — a red bar below the line survives
 * being printed in greyscale and being looked at by a red-green colour-blind
 * operator, which a red-vs-green bar chart alone does not.
 */
export function DivergingBars({
  categories,
  values,
  height = 200,
  format,
  label,
  emptyMessage,
  maxLabels = 7,
  cumulativeValues,
  cumulativeLabel = 'Cumulative',
  valueLabel = 'Net',
}: DivergingBarsProps) {
  const hasData = values.some((v) => Number.isFinite(v) && v !== 0)

  return (
    <ChartShell height={height} label={label} isEmpty={!hasData} empty={emptyMessage}>
      {(plot) => (
        <DivergingBarsBody
          plot={plot}
          categories={categories}
          values={values}
          format={format}
          maxLabels={maxLabels}
          cumulativeValues={cumulativeValues}
          cumulativeLabel={cumulativeLabel}
          valueLabel={valueLabel}
        />
      )}
    </ChartShell>
  )
}

/** Split out so the hooks below run unconditionally — ChartShell may skip its
    children entirely when the chart is empty or not yet measured. */
function DivergingBarsBody({
  plot,
  categories,
  values,
  format,
  maxLabels,
  cumulativeValues,
  cumulativeLabel,
  valueLabel,
}: {
  plot: Plot
  categories: string[]
  values: number[]
  format: (value: number) => string
  maxLabels: number
  cumulativeValues: number[] | undefined
  cumulativeLabel: string
  valueLabel: string
}) {
  const tooltip = useChartTooltip()
  const { margin, inner } = plot
  const x = bandScale(categories.length, [0, inner.width], 0.3)
  const all = cumulativeValues ? [...values, ...cumulativeValues] : values
  const domain = niceDomain(all, { includeZero: true })
  const y = linearScale(domain, [inner.height, 0])
  const zero = y(0)
  const { hovered, overlay } = useCategoryHover(plot, categories.length, x.indexAt)

  const onMove = (event: React.PointerEvent<SVGGElement>) => {
    if (hovered === null) return
    const rows = [
      { label: valueLabel, value: format(values[hovered] ?? 0), color: toneColor(values[hovered] ?? 0) },
    ]
    if (cumulativeValues) {
      rows.push({
        label: cumulativeLabel,
        value: format(cumulativeValues[hovered] ?? 0),
        color: 'var(--w7-c1)',
      })
    }
    tooltip.show({ x: event.clientX, y: event.clientY, title: categories[hovered] ?? '', rows })
  }

  return (
    <g onPointerMove={onMove} onPointerLeave={tooltip.hide}>
      <AxisLeft plot={plot} ticks={niceTicks(domain[0], domain[1], 4)} scale={y} format={format} />
      <AxisBottom
        plot={plot}
        count={categories.length}
        position={x.center}
        format={(i) => categories[i] ?? ''}
        maxLabels={maxLabels}
      />
      <g transform={`translate(${margin.left} ${margin.top})`}>
        {values.map((v, i) => {
          const top = Math.min(zero, y(v))
          const h = Math.max(1, Math.abs(y(v) - zero))
          const up = v >= 0
          return (
            <path
              key={i}
              d={barPath(x(i), top, x.bandwidth, h, up ? [2, 2, 0, 0] : [0, 0, 2, 2])}
              fill={toneColor(v)}
              fillOpacity={hovered === null || hovered === i ? 0.85 : 0.4}
            />
          )
        })}
        {cumulativeValues ? (
          <path
            d={linePath(
              cumulativeValues.map((v, i) => [x.center(i), y(v)] as [number, number]),
              true,
            )}
            fill="none"
            stroke="var(--w7-c1)"
            strokeWidth={1.9}
            strokeLinecap="round"
          />
        ) : null}
      </g>
      {hovered !== null ? <Crosshair plot={plot} x={x.center(hovered)} /> : null}
      {overlay}
    </g>
  )
}

/* --------------------------------------------------------- stacked bars -- */

export interface StackedSeries {
  id: string
  label: string
  values: number[]
  color?: string
}

export interface StackedBarsProps {
  categories: string[]
  series: StackedSeries[]
  height?: number
  format: (value: number) => string
  label: string
  emptyMessage?: string
  maxLabels?: number
  /** Render each series mirrored around zero instead of stacked upward. */
  diverging?: boolean
}

/** Composition over time — credit in vs out, turnover by product. */
export function StackedBars({
  categories,
  series,
  height = 200,
  format,
  label,
  emptyMessage,
  maxLabels = 7,
  diverging = false,
}: StackedBarsProps) {
  const hasData = series.some((s) => s.values.some((v) => v !== 0))

  return (
    <ChartShell height={height} label={label} isEmpty={!hasData} empty={emptyMessage}>
      {(plot) => (
        <StackedBarsBody
          plot={plot}
          categories={categories}
          series={series}
          format={format}
          maxLabels={maxLabels}
          diverging={diverging}
        />
      )}
    </ChartShell>
  )
}

function StackedBarsBody({
  plot,
  categories,
  series,
  format,
  maxLabels,
  diverging,
}: {
  plot: Plot
  categories: string[]
  series: StackedSeries[]
  format: (value: number) => string
  maxLabels: number
  diverging: boolean
}) {
  const tooltip = useChartTooltip()
  const { margin, inner } = plot
  const x = bandScale(categories.length, [0, inner.width], 0.3)

  const totals = categories.map((_, i) =>
    series.reduce((sum, s) => sum + Math.max(0, s.values[i] ?? 0), 0),
  )
  const negTotals = categories.map((_, i) =>
    series.reduce((sum, s) => sum + Math.min(0, s.values[i] ?? 0), 0),
  )
  const domain = niceDomain([...totals, ...negTotals, 0], { includeZero: true })
  const y = linearScale(domain, [inner.height, 0])
  const { hovered, overlay } = useCategoryHover(plot, categories.length, x.indexAt)

  const onMove = (event: React.PointerEvent<SVGGElement>) => {
    if (hovered === null) return
    tooltip.show({
      x: event.clientX,
      y: event.clientY,
      title: categories[hovered] ?? '',
      rows: series.map((s, i) => ({
        label: s.label,
        color: s.color ?? seriesColor(i),
        value: format(s.values[hovered] ?? 0),
      })),
    })
  }

  return (
    <g onPointerMove={onMove} onPointerLeave={tooltip.hide}>
      <AxisLeft plot={plot} ticks={niceTicks(domain[0], domain[1], 4)} scale={y} format={format} />
      <AxisBottom
        plot={plot}
        count={categories.length}
        position={x.center}
        format={(i) => categories[i] ?? ''}
        maxLabels={maxLabels}
      />
      <g transform={`translate(${margin.left} ${margin.top})`}>
        {categories.map((_, i) => {
          let up = 0
          let down = 0
          return (
            <g key={i}>
              {series.map((s, si) => {
                const raw = s.values[i] ?? 0
                if (raw === 0) return null
                const positive = diverging ? si % 2 === 0 : raw >= 0
                const magnitude = Math.abs(raw)
                const base = positive ? up : down
                const y0 = y(positive ? base : -base)
                const y1 = y(positive ? base + magnitude : -(base + magnitude))
                if (positive) up += magnitude
                else down += magnitude
                return (
                  <rect
                    key={s.id}
                    x={x(i)}
                    y={Math.min(y0, y1)}
                    width={x.bandwidth}
                    height={Math.max(1, Math.abs(y1 - y0))}
                    fill={s.color ?? seriesColor(si)}
                    fillOpacity={hovered === null || hovered === i ? 0.85 : 0.4}
                  />
                )
              })}
            </g>
          )
        })}
      </g>
      {hovered !== null ? <Crosshair plot={plot} x={x.center(hovered)} /> : null}
      {overlay}
    </g>
  )
}
